import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface AuthContextProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response | null>;
  getCookie: any;
  setCookie: any;
  removeCookie: any;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const apiToken = getCookie('apiToken');
    if (apiToken) {
      fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${apiToken}` },
      })
        .then((res) => res.ok ? res.json() : null)
        .then((userData) => {
          if (userData) setUser(userData);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // SSO 인증은 서버(/auth/login)가 직접 수행한다. 브라우저는 SSO 결과를 서버에 "전달"하지 않는다.
  // 응답: { user, token } — user.userId 는 SSO 사용자 id 와 같은 값이다.
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, { email, password }, {
        headers: { 'Content-Type': 'application/json' },
      });

      const { user: userData, token } = response.data ?? {};
      if (token && userData) {
        setCookie('userId', userData.userId);
        setCookie('apiToken', token);
        setUser(userData);
        return true;
      }
      return false;
    } catch (error: any) {
      // 401: 아이디/비밀번호 오류, 409: 계정 연결 불가, 429: 시도 과다, 503: SSO 장애
      // 비밀번호가 포함된 요청 객체를 그대로 로깅하지 않는다.
      console.error('Login failed', error?.response?.status ?? 'network error');
      removeCookie('apiToken');
      removeCookie('userId');
      return false;
    }
  };

  const refreshUser = async () => {
    const apiToken = getCookie('apiToken');
    if (!apiToken) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${apiToken}` },
      });
      if (res.ok) setUser(await res.json());
    } catch {}
  };

  const logout = () => {
    // 서버 측 토큰 폐기 (현재 기기만). 실패해도 로컬 로그아웃은 진행한다.
    const apiToken = getCookie('apiToken');
    if (apiToken) {
      fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiToken}` },
        keepalive: true,
      }).catch(() => {});
    }
    removeCookie('apiToken');
    removeCookie('userId');
    setUser(null);
    navigate('/login');
  };

  const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response | null> => {
    const apiToken = getCookie('apiToken');
    const res = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}),
      },
    });
    if (res.status === 401) {
      logout();
      return null;
    }
    return res;
  };

  return (
      <AuthContext.Provider value={{ isAuthenticated: !!user, isAdmin: !!user?.is_admin, user, loading, login, logout, refreshUser, fetchWithAuth, getCookie, setCookie, removeCookie }}>
        {children}
      </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Utility functions to handle cookies
export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
};

export const setCookie = (name: string, value: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; path=/; max-age=2592000${secure}`;
};

export const removeCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
};
